import React, { useEffect, useMemo, useState } from 'react';
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameMonth,
  parseISO,
  startOfMonth,
  startOfWeek,
} from 'date-fns';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import { assignmentsForJob, departmentColor, jobById, TODAY, workTypeLabel } from '../../utils/projectsUtils';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Read-only month calendar of one job's crew assignments, anchored to where the work
// actually is (the earliest assignment) rather than today.
const JobCrewCalendar: React.FC<{ jobId: string }> = ({ jobId }) => {
  const assignments = useMemo(() => assignmentsForJob(jobId).filter((a) => a.date), [jobId]);
  const accent = departmentColor(jobById(jobId)?.dept_id || '');

  const firstMonth = useMemo(() => {
    const dates = assignments.map((a) => a.date as string).sort();
    return startOfMonth(dates[0] ? parseISO(dates[0]) : parseISO(TODAY));
  }, [assignments]);

  const [cursor, setCursor] = useState<Date>(firstMonth);
  useEffect(() => setCursor(firstMonth), [firstMonth]);

  const byDate = useMemo(() => {
    const map = new Map<string, typeof assignments>();
    assignments.forEach((a) => {
      const key = a.date as string;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(a);
    });
    return map;
  }, [assignments]);

  const days = useMemo(
    () => eachDayOfInterval({ start: startOfWeek(startOfMonth(cursor)), end: endOfWeek(endOfMonth(cursor)) }),
    [cursor],
  );

  const monthCrewDays = days.reduce(
    (sum, day) => (isSameMonth(day, cursor) ? sum + (byDate.get(format(day, 'yyyy-MM-dd'))?.length || 0) : sum),
    0,
  );

  return (
    <Card variant="outlined" sx={{ overflowX: 'auto' }}>
      <Box sx={{ minWidth: 760 }}>
        {/* Month nav */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5 }}>
          <IconButton size="small" onClick={() => setCursor((c) => addMonths(c, -1))}>
            <ChevronLeftIcon />
          </IconButton>
          <Typography variant="subtitle1" fontWeight={700} sx={{ minWidth: 160, textAlign: 'center' }}>
            {format(cursor, 'MMMM yyyy')}
          </Typography>
          <IconButton size="small" onClick={() => setCursor((c) => addMonths(c, 1))}>
            <ChevronRightIcon />
          </IconButton>
          <Button size="small" onClick={() => setCursor(startOfMonth(parseISO(TODAY)))}>
            Today
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          <Typography variant="body2" color="text.secondary">
            {monthCrewDays} crew-day{monthCrewDays === 1 ? '' : 's'} this month
          </Typography>
        </Box>

        {/* Weekday header */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
          {WEEKDAYS.map((weekday) => (
            <Box
              key={weekday}
              sx={{ p: 0.5, textAlign: 'center', backgroundColor: 'grey.100', borderBottom: 1, borderColor: 'divider' }}
            >
              <Typography variant="caption" fontWeight={700}>
                {weekday.toUpperCase()}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Day cells */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
          {days.map((day) => {
            const iso = format(day, 'yyyy-MM-dd');
            const inMonth = isSameMonth(day, cursor);
            const isToday = iso === TODAY;
            const crew = byDate.get(iso) || [];
            return (
              <Box
                key={iso}
                sx={{
                  minHeight: 92,
                  p: 0.75,
                  borderBottom: 1,
                  borderRight: 1,
                  borderColor: 'divider',
                  opacity: inMonth ? 1 : 0.4,
                  backgroundColor: isToday ? 'primary.50' : 'transparent',
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Typography variant="caption" fontWeight={isToday ? 700 : 500} color="text.secondary">
                    {format(day, 'd')}
                  </Typography>
                </Box>
                {inMonth && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, mt: 0.25 }}>
                    {crew.map((member, index) => (
                      <Tooltip
                        key={`${member.emp_id}-${index}`}
                        arrow
                        title={`${member.tech_name} · #${member.order_no} · ${workTypeLabel(member.work_type)} · ${
                          member.start_time
                        } · ${member.duration}h`}
                      >
                        <Box
                          sx={{
                            backgroundColor: accent,
                            color: 'white',
                            borderRadius: 0.5,
                            px: 0.5,
                            py: 0.1,
                            fontSize: 10.5,
                            lineHeight: 1.4,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {member.tech_name} · {member.duration}h
                        </Box>
                      </Tooltip>
                    ))}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      </Box>
    </Card>
  );
};

export default JobCrewCalendar;
